import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import {
  useStructuresByUserGroupQuery,
  useRemoveUserGroupsFromStructureMutation,
  StructuresByUserGroupDocument,
  AllStructuresForAddPlatformDialogDocument,
} from '@/__generated__/graphql';
import { ConfirmationDialog } from '../Dialogs/ConfirmationDialog';
import { PlatformsListing } from './PlatformsListing';
import { useAtom } from 'jotai';
import { userManagementSearchTerm } from '../state';
import { useDebounce } from '@/hooks/useDebounce';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { abyssColors, userManagementTableBorderColor } from '@/theme/colors';
import { TableDataNotFound } from '../shared/TableDataNotFound';
import { TableSkeleton } from '../shared/TableSkeleton';
import { TablePagination } from '../../shared/TablePagination';
import { defaultPaginationSize } from '@/constants';

type Props = {
  selectedGroupID: string;
};

type Platform = {
  id: string;
  name: string;
  coverImagePath: string;
  description: string;
};

export const PlatformDetail = ({ selectedGroupID }: Props) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [deletePlatform, setDeletePlatform] = React.useState<Platform | null>();

  const [searchValue, setSearchTerm] = useAtom(userManagementSearchTerm);
  const searchTerm = useDebounce(searchValue, 700);
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  useEffect(() => {
    return () => {
      setSearchTerm('');
    };
  }, [setSearchTerm]);

  const { loading, data } = useStructuresByUserGroupQuery({
    variables: {
      structuresInput: {
        userGroupId: selectedGroupID,
        name: searchTerm,
      },
      pageInfo: {
        skip: page * rowsPerPage,
        limit: rowsPerPage,
      },
      input: { userGroupId: selectedGroupID, name: searchTerm },
    },
    skip: !selectedGroupID,
  });

  const { structuresByUserGroup, structuresCount } = useMemo(() => {
    return {
      structuresByUserGroup: data?.structuresByUserGroup ?? [],
      structuresCount: data?.structuresCount?.count ?? 0,
    };
  }, [data]);

  const handleDeleteConfirmationClose = () => {
    setDeletePlatform(undefined);
  };

  const handleDeleteConfirmationOpen = (platform: Platform) => {
    setDeletePlatform(platform);
  };

  const [removeStructuresFromUserGroupMutation] = useRemoveUserGroupsFromStructureMutation();
  const handleRemoveUserFromGroup = useCallback(() => {
    if (deletePlatform) {
      removeStructuresFromUserGroupMutation({
        variables: {
          input: {
            structureIds: [deletePlatform?.id],
            userGroupIds: [selectedGroupID],
          },
        },
        refetchQueries: [StructuresByUserGroupDocument, AllStructuresForAddPlatformDialogDocument],
      });
      handleDeleteConfirmationClose();
      showSnackBar(`${deletePlatform?.name} has been deleted successfully`, 'success');
    }
  }, [deletePlatform, removeStructuresFromUserGroupMutation, selectedGroupID, showSnackBar]);

  const handleChangePage = (newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  return (
    <Box sx={{ width: '100%', pt: 7 }}>
      <Typography
        sx={{ fontSize: '12px', fontWeight: 600, pl: 2, color: abyssColors.primary[500] }}
      >
        PLATFORM NAME
      </Typography>
      {loading && <TableSkeleton noOfSimpleColumns={1} />}

      <Box
        sx={{
          mt: 2,
          borderRadius: 2,
          border: loading || structuresByUserGroup.length === 0 ? 0 : 1,
          borderColor: userManagementTableBorderColor,
        }}
      >
        {!loading && (
          <PlatformsListing
            platformsList={structuresByUserGroup}
            handleDeleteConfirmationOpen={handleDeleteConfirmationOpen}
            imageStyles={{ width: '80px', borderRadius: '4px' }}
          />
        )}
        {!loading && structuresByUserGroup.length === 0 && (
          <TableDataNotFound message="No Platforms Found" />
        )}
        {!loading && structuresByUserGroup.length > 0 && (
          <TablePagination
            count={structuresCount}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[
              ...defaultPaginationSize,
              { label: 'All', value: structuresCount },
            ]}
          />
        )}
        {!!deletePlatform && (
          <ConfirmationDialog
            open={!!deletePlatform}
            title={`Removing Platform from the group ${deletePlatform?.name}`}
            message={`Are you sure you want to remove ${deletePlatform?.name} from this group? Users in this group will no longer have access to these platform(s).`}
            buttonText="Remove Platform from Group"
            handleRemoveUserFromGroup={handleRemoveUserFromGroup}
            onClose={handleDeleteConfirmationClose}
          />
        )}
      </Box>
    </Box>
  );
};
